import type { WebchatConfigForm } from "@ciptex/conversation-sdk";
import { Box } from "@twilio-paste/core/box";
import { Label } from "@twilio-paste/core/label";
import { Option, Select } from "@twilio-paste/core/select";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { createSelector } from "reselect";
import { useValidation } from "../../providers/ValidationContext";
import { updatePreEngagementData } from "../../store/actions/genericActions";
import { ChatReducer } from "../../store/chat.reducer";
import type { AppState } from "../../store/definitions";
import { SessionReducer } from "../../store/session.reducer";
import { fieldStyles } from "../styles/PreEngagementFormPhase.styles";

type Field = NonNullable<WebchatConfigForm['fields']>[number];

interface SelectFieldProps {
    field: Field & { type: "select" };
}

interface Option {
    label: string;
    value: string;
}

const selectFieldSelector = createSelector(
    (state: AppState) => state,
    (state) => ({ preEngagementData: state.session.preEngagementData })
);

export const SelectField = ({ field }: SelectFieldProps) => {
    const { preEngagementData } = useSelector(selectFieldSelector) || {};
    const dispatch = useDispatch<ThunkDispatch<ChatReducer | SessionReducer, AnyAction, AnyAction>>();
    const { validateField } = useValidation();

    return (
        <Box {...fieldStyles}>
            <Label htmlFor={field.id}>{field.label}</Label>
            <Select
                element="DEFAULT_SELECT"
                name={field.id}
                id={field.id}
                data-test={`pre-engagement-chat-form-${field.id}-input`}
                value={preEngagementData?.[field.id] ?? ""}
                onChange={(e) => {
                    dispatch(updatePreEngagementData({ [field.id]: e.target.value }));
                    validateField(field.id, e.target.value);
                }}
                autoComplete={field.id}
                required={field.required}
            >
                {
                    field.options && field.options.length > 0 ?
                        (
                            <>
                                <Option key={`${field.id}-empty`} value="">{""}</Option>
                                {
                                    field.options?.map((option: Option) => (
                                        <Option
                                            key={`${field.id}-${option.value}`}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </Option>
                                    ))
                                }
                            </>
                        ) :
                        <Option value="">
                            No options available
                        </Option>
                }
            </Select>
        </Box>
    );
}